import {getOwnedNodes, meshNodesToBinaryStl, SceneNodes, SceneProperties, TemplateGraph, Parameters} from "@cm/template-nodes"
import {Injectable, inject} from "@angular/core"
import type {CmLogger} from "@cm/log"
import {SceneManagerService} from "@app/template-editor/services/scene-manager.service"
import {CM_LOGGER_SCENE} from "@common/services/logging"

@Injectable({
    providedIn: "root",
})
export class StlExportService {
    private readonly logger: CmLogger = inject(CM_LOGGER_SCENE)

    async exportStlFiles(
        templateGraph: TemplateGraph,
        parameters: Parameters,
        sceneOptions:
            | {
                  maxSubdivisionLevel: number
                  maxSubdivisionLevelOnMobile?: number
              }
            | undefined,
        sceneManagerService: SceneManagerService,
    ): Promise<Blob> {
        const clonedTemplateGraph = templateGraph.clone({cloneSubNode: () => true})

        const sceneProperties = new SceneProperties({
            parameters: {
                maxSubdivisionLevel: sceneOptions?.maxSubdivisionLevel ?? 9999,
                maxSubdivisionLevelOnMobile: sceneOptions?.maxSubdivisionLevelOnMobile ?? sceneOptions?.maxSubdivisionLevel ?? 9999,
                uiColor: [0, 0, 0],
                shiftAndZoomBackgroundImage: false,
                uiStyle: "default",
                iconSize: 24,
                enableAr: false,
                enableSalesEnquiry: false,
                textureResolution: "2000px",
                textureFiltering: false,
                textureStreamingMode: "off",
                enableRealtimeShadows: false,
                enableRealtimeLights: false,
                enableRealtimeMaterials: false,
                enableOnboardingHint: false,
                enableGltfDownload: false,
                enableStlDownload: false,
                enableSnapshot: false,
                enablePdfDownload: false,
                enableFullscreen: false,
                environmentMapMode: "full",
                showAnnotations: false,
                enableDimensionGuides: false,
                enableAdaptiveSubdivision: false,
                verticalArPlacement: false,
            },
        })
        clonedTemplateGraph.parameters.nodes.replaceParameters({list: [sceneProperties, ...getOwnedNodes(clonedTemplateGraph)]})

        sceneManagerService.$templateGraph.set(clonedTemplateGraph)
        sceneManagerService.$instanceParameters.set(parameters)

        sceneManagerService.compileTemplate()
        await sceneManagerService.sync(true)

        const meshesHighres = sceneManagerService.$scene().filter((x): x is SceneNodes.Mesh => SceneNodes.Mesh.is(x) && (!x.isProcedural || x.isDecal))
        const binaryStlDataHighres = meshNodesToBinaryStl(meshesHighres, this.logger)

        sceneProperties.updateParameters({maxSubdivisionLevel: 0, maxSubdivisionLevelOnMobile: 0})
        sceneManagerService.compileTemplate()
        await sceneManagerService.sync(true)

        const meshesLowres = sceneManagerService.$scene().filter((x): x is SceneNodes.Mesh => SceneNodes.Mesh.is(x) && (!x.isProcedural || x.isDecal))
        const binaryStlDataLowres = meshNodesToBinaryStl(meshesLowres, this.logger)

        const {default: JSZip} = await import("jszip")
        const zip = new JSZip()
        if (binaryStlDataHighres.triangleCount === binaryStlDataLowres.triangleCount) {
            zip.file("mesh.stl", binaryStlDataHighres.blob)
        } else {
            zip.file("mesh_highres.stl", binaryStlDataHighres.blob)
            zip.file("mesh_lowres.stl", binaryStlDataLowres.blob)
        }

        return zip.generateAsync({type: "blob"})
    }
}
